import { TEXT, TEXT_MUTED, TEAL } from "../../components/marketing/tokens";

/* ══════════════════════════════════════════════════════════════
   MOCKUP VISUALS - one per integration
   ══════════════════════════════════════════════════════════════ */

function GlassCard({ children, style }) {
  return (
    <div style={{
      background: "rgba(255,255,255,0.02)",
      border: "1px solid rgba(255,255,255,0.06)",
      borderRadius: 14, padding: 24, ...style,
    }}>
      {children}
    </div>
  );
}

/* 1. Google Search Console - Search Shift comparison */
function GSCMockup() {
  const queries = [
    { query: "best crm for startups", googleRank: "#3", aiVisible: false, status: "Opportunity", statusColor: TEAL },
    { query: "how to choose a crm", googleRank: "#1", aiVisible: true, status: "Healthy", statusColor: "#34d399" },
    { query: "crm pricing comparison", googleRank: "#5", aiVisible: false, status: "Opportunity", statusColor: TEAL },
    { query: "crm vs spreadsheet", googleRank: "#2", aiVisible: false, status: "At Risk", statusColor: "#f59e0b" },
    { query: "saas crm features", googleRank: "#4", aiVisible: true, status: "Healthy", statusColor: "#34d399" },
  ];
  return (
    <GlassCard>
      <div style={{ fontFamily: "var(--display)", fontSize: 14, fontWeight: 600, color: TEXT, marginBottom: 16 }}>
        Search Shift Analysis
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 72px 72px 90px", gap: "0", fontSize: 12, fontFamily: "var(--mono)" }}>
        {/* Header */}
        <div style={{ color: TEXT_MUTED, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>Query</div>
        <div style={{ color: TEXT_MUTED, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", textAlign: "center" }}>Google</div>
        <div style={{ color: TEXT_MUTED, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", textAlign: "center" }}>AI</div>
        <div style={{ color: TEXT_MUTED, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", textAlign: "right" }}>Status</div>
        {/* Rows */}
        {queries.map((q, i) => (
          <div key={i} style={{ display: "contents" }}>
            <div style={{ color: TEXT, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.03)", fontSize: 12, lineHeight: 1.3 }}>{q.query}</div>
            <div style={{ color: "#34d399", padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.03)", textAlign: "center", fontWeight: 600 }}>{q.googleRank}</div>
            <div style={{ padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.03)", textAlign: "center" }}>
              <span style={{
                display: "inline-block", width: 8, height: 8, borderRadius: "50%",
                background: q.aiVisible ? "#34d399" : "rgba(255,255,255,0.15)",
              }} />
            </div>
            <div style={{
              padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.03)", textAlign: "right",
              color: q.statusColor, fontSize: 11, fontWeight: 600,
            }}>{q.status}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>
        3 of 5 top Google queries have no AI visibility
      </div>
    </GlassCard>
  );
}

/* 2. Google Analytics 4 - Dual line chart */
function GA4Mockup() {
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun"];
  const sessions = [4200, 4400, 4100, 4600, 4900, 5200];
  const aiVisits = [120, 280, 410, 620, 890, 1340];
  const maxVal = 5500;
  const chartH = 140;
  const chartW = 320;

  function toPoints(data) {
    return data.map((v, i) => {
      const x = (i / (data.length - 1)) * chartW;
      const y = chartH - (v / maxVal) * chartH;
      return `${x},${y}`;
    }).join(" ");
  }

  return (
    <GlassCard>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ fontFamily: "var(--display)", fontSize: 14, fontWeight: 600, color: TEXT }}>
          Traffic Overview (6 months)
        </div>
        <div style={{ display: "flex", gap: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 10, height: 3, borderRadius: 2, background: "rgba(255,255,255,0.4)" }} />
            <span style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>Sessions</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 10, height: 3, borderRadius: 2, background: TEAL }} />
            <span style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>AI Bot Visits</span>
          </div>
        </div>
      </div>
      <div style={{ position: "relative", width: "100%", maxWidth: chartW + 40 }}>
        <svg viewBox={`-30 -10 ${chartW + 40} ${chartH + 30}`} style={{ width: "100%", height: "auto" }}>
          {/* Y-axis labels */}
          {[0, 2500, 5000].map((v) => {
            const y = chartH - (v / maxVal) * chartH;
            return (
              <g key={v}>
                <text x={-8} y={y + 3} textAnchor="end" style={{ fontSize: 10, fill: TEXT_MUTED, fontFamily: "var(--mono)" }}>
                  {v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                </text>
                <line x1={0} y1={y} x2={chartW} y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth={1} />
              </g>
            );
          })}
          {/* X-axis labels */}
          {months.map((m, i) => {
            const x = (i / (months.length - 1)) * chartW;
            return (
              <text key={m} x={x} y={chartH + 18} textAnchor="middle" style={{ fontSize: 10, fill: TEXT_MUTED, fontFamily: "var(--mono)" }}>
                {m}
              </text>
            );
          })}
          {/* Sessions line */}
          <polyline points={toPoints(sessions)} fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          {/* AI Visits line */}
          <polyline points={toPoints(aiVisits)} fill="none" stroke={TEAL} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
          {/* AI Visits area fill */}
          <polygon
            points={`0,${chartH} ${toPoints(aiVisits)} ${chartW},${chartH}`}
            fill="url(#tealGrad)" opacity={0.15}
          />
          <defs>
            <linearGradient id="tealGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={TEAL} stopOpacity={0.4} />
              <stop offset="100%" stopColor={TEAL} stopOpacity={0} />
            </linearGradient>
          </defs>
        </svg>
      </div>
      <div style={{ display: "flex", gap: 24, marginTop: 12 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: TEXT, fontFamily: "var(--display)" }}>5,200</div>
          <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>Sessions (Jun)</div>
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: TEAL, fontFamily: "var(--display)" }}>1,340</div>
          <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>AI Bot Visits (Jun)</div>
        </div>
        <div>
          <div style={{ fontSize: 20, fontWeight: 700, color: "#34d399", fontFamily: "var(--display)" }}>+1,017%</div>
          <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>AI Growth (6mo)</div>
        </div>
      </div>
    </GlassCard>
  );
}

/* 3. Google Tag Manager - 3-step connection flow */
function GTMMockup() {
  const steps = [
    { step: 1, label: "Connect", desc: "Authorize GTM access", icon: "🔗", active: true, done: true },
    { step: 2, label: "Select", desc: "Choose container", icon: "📦", active: true, done: true },
    { step: 3, label: "Done", desc: "Snippet installed", icon: "✓", active: true, done: true },
  ];
  return (
    <GlassCard>
      <div style={{ fontFamily: "var(--display)", fontSize: 14, fontWeight: 600, color: TEXT, marginBottom: 20 }}>
        GTM Setup
      </div>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 0, position: "relative" }}>
        {steps.map((s, i) => (
          <div key={i} style={{ flex: 1, textAlign: "center", position: "relative" }}>
            {/* Connector line - starts after this circle, ends before the next */}
            {i < steps.length - 1 && (
              <div style={{
                position: "absolute", top: 20, left: "calc(50% + 24px)", width: "calc(100% - 48px)", height: 2,
                background: s.done ? TEAL : "rgba(255,255,255,0.08)",
                zIndex: 0,
              }} />
            )}
            {/* Circle */}
            <div style={{
              width: 40, height: 40, borderRadius: "50%",
              background: s.done ? "rgba(79,205,196,0.15)" : "rgba(255,255,255,0.04)",
              border: `2px solid ${s.done ? TEAL : "rgba(255,255,255,0.08)"}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              margin: "0 auto 10px", position: "relative", zIndex: 1,
              fontSize: s.step === 3 ? 18 : 16,
            }}>
              {s.step === 3 ? (
                <span style={{ color: TEAL, fontWeight: 700 }}>&#10003;</span>
              ) : (
                <span style={{ color: s.done ? TEAL : TEXT_MUTED, fontWeight: 600, fontSize: 14, fontFamily: "var(--mono)" }}>{s.step}</span>
              )}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: TEXT, fontFamily: "var(--display)", marginBottom: 2 }}>{s.label}</div>
            <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>{s.desc}</div>
          </div>
        ))}
      </div>
      {/* Container selector */}
      <div style={{
        marginTop: 24, padding: "14px 16px", borderRadius: 10,
        background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontSize: 12, color: TEXT_MUTED, fontFamily: "var(--body)", marginBottom: 2 }}>Container</div>
          <div style={{ fontSize: 14, color: TEXT, fontFamily: "var(--mono)", fontWeight: 500 }}>GTM-PKTFRDMZ</div>
        </div>
        <div style={{
          padding: "6px 14px", borderRadius: 8,
          background: "rgba(79,205,196,0.12)", color: TEAL,
          fontSize: 12, fontWeight: 600, fontFamily: "var(--body)",
        }}>
          Connected
        </div>
      </div>
      <div style={{ marginTop: 14, fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>
        No developer required - setup takes under 2 minutes
      </div>
    </GlassCard>
  );
}

/* 4. Slack - Weekly briefing message */
function SlackMockup() {
  return (
    <GlassCard style={{ padding: 0, overflow: "hidden" }}>
      {/* Slack header bar */}
      <div style={{
        padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ fontSize: 14 }}>#</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: TEXT, fontFamily: "var(--body)" }}>marketing-ai-updates</span>
      </div>
      {/* Message */}
      <div style={{ padding: "16px 20px" }}>
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 6, flexShrink: 0,
            background: "rgba(79,205,196,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, fontWeight: 700, color: TEAL,
          }}>
            &#10022;
          </div>
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: TEXT, fontFamily: "var(--body)" }}>AI-Advisors</span>
              <span style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>9:00 AM</span>
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: TEXT, fontFamily: "var(--body)", marginBottom: 10 }}>
              Weekly AI Visibility Briefing - Mar 31, 2026
            </div>
          </div>
        </div>
        {/* Stats block */}
        <div style={{
          background: "rgba(255,255,255,0.02)", borderRadius: 8,
          borderLeft: `3px solid ${TEAL}`, padding: "12px 16px", marginLeft: 42,
        }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>Brand Mentions</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: TEXT, fontFamily: "var(--display)" }}>
                47 <span style={{ fontSize: 11, color: "#34d399", fontWeight: 600 }}>+12</span>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>Citation Share</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: TEXT, fontFamily: "var(--display)" }}>
                23% <span style={{ fontSize: 11, color: "#34d399", fontWeight: 600 }}>+4%</span>
              </div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: TEXT_MUTED, fontFamily: "var(--body)", lineHeight: 1.5 }}>
            <span style={{ color: TEAL, fontWeight: 600 }}>New citation:</span> Perplexity now cites your pricing page for "best CRM for startups"
          </div>
          <div style={{ fontSize: 12, color: TEXT_MUTED, fontFamily: "var(--body)", lineHeight: 1.5, marginTop: 4 }}>
            <span style={{ color: "#f59e0b", fontWeight: 600 }}>Watch:</span> Competitor "RivalCRM" gained 3 new citations this week
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

/* 5. WordPress - Auto-applied fixes feed */
function WordPressMockup() {
  const items = [
    { label: "robots.txt updated", meta: "+2 pts", time: "2h ago", status: "applied" },
    { label: "llms.txt generated", meta: "+3 pts", time: "2h ago", status: "applied" },
    { label: "FAQ schema on /pricing", meta: "+1 pt", time: "5h ago", status: "applied" },
    { label: "Meta description rewrite", meta: "2 pages", time: "Pending", status: "review" },
    { label: "Blog draft: \"How to rank in AI search\"", meta: "Draft", time: "Pending", status: "draft" },
  ];

  return (
    <GlassCard style={{ padding: 0, overflow: "hidden" }}>
      {/* Score header */}
      <div style={{
        padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)", marginBottom: 2 }}>AEO Score</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
            <span style={{ fontSize: 28, fontWeight: 700, color: TEXT, fontFamily: "var(--display)" }}>82</span>
            <span style={{ fontSize: 12, fontWeight: 600, color: "#34d399", fontFamily: "var(--mono)" }}>+6 this week</span>
          </div>
        </div>
        <div style={{
          padding: "6px 12px", borderRadius: 6,
          background: "rgba(79,205,196,0.12)", color: TEAL,
          fontSize: 11, fontWeight: 600, fontFamily: "var(--body)",
          display: "flex", alignItems: "center", gap: 6,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: TEAL }} />
          Plugin connected
        </div>
      </div>
      {/* Activity feed */}
      <div style={{ padding: "8px 0" }}>
        <div style={{
          padding: "8px 20px", fontSize: 10, color: TEXT_MUTED,
          fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: "0.08em",
        }}>
          Recent activity
        </div>
        {items.map((item, i) => {
          const statusConfig = {
            applied: { color: "#34d399", label: "Applied", icon: "\u2713" },
            review: { color: "#f59e0b", label: "Review", icon: "\u25CB" },
            draft: { color: TEAL, label: "Draft", icon: "\u270E" },
          }[item.status];
          return (
            <div key={i} style={{
              padding: "10px 20px", display: "flex", alignItems: "center", gap: 12,
              borderTop: i > 0 ? "1px solid rgba(255,255,255,0.03)" : "none",
            }}>
              <div style={{
                width: 24, height: 24, borderRadius: 6, flexShrink: 0,
                background: `${statusConfig.color}1a`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 12, color: statusConfig.color, fontWeight: 700,
              }}>
                {statusConfig.icon}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 13, color: TEXT, fontFamily: "var(--body)", fontWeight: 500,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                }}>
                  {item.label}
                </div>
                <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)", marginTop: 1 }}>
                  {item.meta} · {item.time}
                </div>
              </div>
              <span style={{
                fontSize: 10, fontWeight: 600, color: statusConfig.color,
                fontFamily: "var(--mono)", textTransform: "uppercase", letterSpacing: "0.05em",
                flexShrink: 0,
              }}>
                {statusConfig.label}
              </span>
            </div>
          );
        })}
      </div>
    </GlassCard>
  );
}

/* 6. Google Sheets - Weekly scorecard with tab switcher */
function GoogleSheetsMockup() {
  const rows = [
    { metric: "Visibility Score", current: "68", change: "+4", positive: true },
    { metric: "Share of Voice", current: "23%", change: "+2%", positive: true },
    { metric: "Avg Position", current: "3.2", change: "-0.4", positive: true },
    { metric: "Citation Share", current: "18%", change: "+3%", positive: true },
  ];
  const tabs = [
    { label: "Visibility", active: true },
    { label: "Prompts", active: false },
    { label: "Citations", active: false },
    { label: "My Dashboard", active: false, custom: true },
  ];
  return (
    <GlassCard style={{ padding: 0, overflow: "hidden" }}>
      {/* Sheet header */}
      <div style={{
        padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)", marginBottom: 2 }}>
            ai-advisors-scorecard
          </div>
          <div style={{
            fontFamily: "var(--display)", fontSize: 14, fontWeight: 600, color: TEXT,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            AI Visibility Scorecard
          </div>
        </div>
        <div style={{
          padding: "6px 12px", borderRadius: 6,
          background: "rgba(79,205,196,0.12)", color: TEAL,
          fontSize: 11, fontWeight: 600, fontFamily: "var(--body)",
          display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: TEAL }} />
          Synced Mon 9:00 AM
        </div>
      </div>
      {/* Data rows */}
      <div style={{ padding: "4px 0" }}>
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 80px 80px",
          padding: "10px 20px", borderBottom: "1px solid rgba(255,255,255,0.04)",
          fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--mono)",
          textTransform: "uppercase", letterSpacing: "0.05em",
        }}>
          <div>Metric</div>
          <div style={{ textAlign: "right" }}>Current</div>
          <div style={{ textAlign: "right" }}>vs Last</div>
        </div>
        {rows.map((row, i) => (
          <div key={i} style={{
            display: "grid", gridTemplateColumns: "1fr 80px 80px",
            padding: "12px 20px", borderTop: i > 0 ? "1px solid rgba(255,255,255,0.03)" : "none",
            alignItems: "center",
          }}>
            <div style={{ fontSize: 13, color: TEXT, fontFamily: "var(--body)", fontWeight: 500 }}>
              {row.metric}
            </div>
            <div style={{
              fontSize: 14, color: TEXT, fontFamily: "var(--mono)",
              fontWeight: 600, textAlign: "right",
            }}>
              {row.current}
            </div>
            <div style={{
              fontSize: 12, color: row.positive ? "#34d399" : "#f59e0b",
              fontFamily: "var(--mono)", fontWeight: 600, textAlign: "right",
            }}>
              {row.change}
            </div>
          </div>
        ))}
      </div>
      {/* Sheet tabs footer */}
      <div style={{
        borderTop: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "stretch", gap: 0, minWidth: 0,
      }}>
        {tabs.map((t, i) => (
          <div key={t.label} style={{
            padding: "9px 8px", flex: t.custom ? "1.6 1 0" : "1 1 0", minWidth: 0,
            borderRight: i < tabs.length - 1 ? "1px solid rgba(255,255,255,0.04)" : "none",
            background: t.active ? "rgba(79,205,196,0.08)" : "transparent",
            borderTop: t.active ? `2px solid ${TEAL}` : "2px solid transparent",
            fontSize: 11, fontWeight: t.active ? 600 : 500,
            color: t.active ? TEAL : TEXT_MUTED,
            fontFamily: "var(--body)",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
            whiteSpace: "nowrap",
          }}>
            {t.custom && (
              <span style={{
                fontSize: 9, padding: "1px 4px", borderRadius: 3,
                background: "rgba(255,255,255,0.06)", color: TEXT_MUTED,
                fontFamily: "var(--mono)", fontWeight: 600, letterSpacing: "0.05em",
                flexShrink: 0,
              }}>YOU</span>
            )}
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>{t.label}</span>
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

/* 7. Google Ads - Coverage view (keyword to prompt conversion) */
function GoogleAdsMockup() {
  const keywords = [
    { keyword: "best crm for sales teams", spend: "$4.8K", tracked: true },
    { keyword: "crm pricing comparison", spend: "$3.2K", tracked: false },
    { keyword: "hubspot vs blaze crm", spend: "$2.7K", tracked: true },
    { keyword: "sales pipeline software", spend: "$2.1K", tracked: false },
    { keyword: "crm for startups", spend: "$1.6K", tracked: false },
  ];
  const trackedCount = keywords.filter((k) => k.tracked).length;
  return (
    <GlassCard>
      <div style={{ fontFamily: "var(--display)", fontSize: 14, fontWeight: 600, color: TEXT, marginBottom: 16 }}>
        Google Ads Coverage
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 60px 90px", gap: "0", fontSize: 12, fontFamily: "var(--mono)" }}>
        <div style={{ color: TEXT_MUTED, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>Keyword</div>
        <div style={{ color: TEXT_MUTED, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", textAlign: "right" }}>Spend</div>
        <div style={{ color: TEXT_MUTED, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", textAlign: "right" }}>Status</div>
        {keywords.map((k, i) => (
          <div key={i} style={{ display: "contents" }}>
            <div style={{ color: TEXT, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.03)", fontSize: 12, lineHeight: 1.3, paddingRight: 8 }}>{k.keyword}</div>
            <div style={{ color: TEXT_MUTED, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.03)", textAlign: "right", fontWeight: 500 }}>{k.spend}</div>
            <div style={{ padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.03)", textAlign: "right" }}>
              {k.tracked ? (
                <span style={{ color: "#34d399", fontSize: 11, fontWeight: 600 }}>&#10003; Tracked</span>
              ) : (
                <span style={{ color: TEAL, fontSize: 11, fontWeight: 600 }}>Convert</span>
              )}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>
        {keywords.length - trackedCount} of {keywords.length} top keywords have no AI prompt yet
      </div>
    </GlassCard>
  );
}

/* 8. ChatGPT Ads - Campaign performance with AEO citation overlay */
function ChatGPTAdsMockup() {
  const campaigns = [
    { name: "Best CRM for sales teams", ctr: "4.2%", aeo: "Cited", aeoColor: "#34d399", aeoIcon: "✓" },
    { name: "Pipeline software 2026", ctr: "3.7%", aeo: "Cited", aeoColor: "#34d399", aeoIcon: "✓" },
    { name: "HubSpot alternatives", ctr: "2.9%", aeo: "Partial", aeoColor: "#f59e0b", aeoIcon: "◐" },
    { name: "Sales automation tools", ctr: "5.1%", aeo: "Gap", aeoColor: TEAL, aeoIcon: "○" },
    { name: "AI for sales teams", ctr: "4.4%", aeo: "Cited", aeoColor: "#34d399", aeoIcon: "✓" },
  ];
  const citedCount = campaigns.filter((c) => c.aeo === "Cited").length;
  return (
    <GlassCard>
      {/* Header with stat strip */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 16 }}>
        <div style={{ fontFamily: "var(--display)", fontSize: 14, fontWeight: 600, color: TEXT }}>
          ChatGPT Ads Performance
        </div>
        <div style={{ display: "flex", gap: 18, flexShrink: 0 }}>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: TEXT, fontFamily: "var(--display)", lineHeight: 1.1 }}>$7.5K</div>
            <div style={{ fontSize: 10, color: TEXT_MUTED, fontFamily: "var(--body)", marginTop: 2 }}>Spend (30d)</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: TEAL, fontFamily: "var(--display)", lineHeight: 1.1 }}>24.1K</div>
            <div style={{ fontSize: 10, color: TEXT_MUTED, fontFamily: "var(--body)", marginTop: 2 }}>Impressions</div>
          </div>
        </div>
      </div>
      {/* Table */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 56px 96px", gap: "0", fontSize: 12, fontFamily: "var(--mono)" }}>
        <div style={{ color: TEXT_MUTED, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>Campaign</div>
        <div style={{ color: TEXT_MUTED, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", textAlign: "right" }}>CTR</div>
        <div style={{ color: TEXT_MUTED, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", textAlign: "right" }}>AEO Match</div>
        {campaigns.map((c, i) => (
          <div key={i} style={{ display: "contents" }}>
            <div style={{ color: TEXT, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.03)", fontSize: 12, lineHeight: 1.3, paddingRight: 8 }}>{c.name}</div>
            <div style={{ color: TEXT_MUTED, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.03)", textAlign: "right", fontWeight: 500 }}>{c.ctr}</div>
            <div style={{ padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.03)", textAlign: "right", color: c.aeoColor, fontSize: 11, fontWeight: 600 }}>
              {c.aeoIcon} {c.aeo}
            </div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14, fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>
        {citedCount} of {campaigns.length} campaigns cited in ChatGPT organic answers
      </div>
    </GlassCard>
  );
}

/* 9. Bing Webmaster Tools - Google vs Bing head-to-head */
function BingMockup() {
  const GOOGLE = "#4285F4";
  const BING = "#00B7C3";
  const rows = [
    { metric: "Clicks", google: "12.4K", bing: "2.2K" },
    { metric: "Impressions", google: "318K", bing: "61K" },
    { metric: "CTR", google: "3.9%", bing: "3.6%" },
  ];
  return (
    <GlassCard>
      {/* Header + legend */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontFamily: "var(--display)", fontSize: 14, fontWeight: 600, color: TEXT }}>
          Google vs Bing
        </div>
        <div style={{ display: "flex", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: GOOGLE }} />
            <span style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>Google</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: BING }} />
            <span style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>Bing</span>
          </div>
        </div>
      </div>
      {/* KPI comparison table */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 84px 84px", gap: "0", fontSize: 12, fontFamily: "var(--mono)" }}>
        <div style={{ color: TEXT_MUTED, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>Metric</div>
        <div style={{ color: TEXT_MUTED, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", textAlign: "right" }}>Google</div>
        <div style={{ color: TEXT_MUTED, padding: "6px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", textAlign: "right" }}>Bing</div>
        {rows.map((r, i) => (
          <div key={i} style={{ display: "contents" }}>
            <div style={{ color: TEXT, padding: "11px 0", borderBottom: "1px solid rgba(255,255,255,0.03)", fontFamily: "var(--body)" }}>{r.metric}</div>
            <div style={{ color: TEXT, padding: "11px 0", borderBottom: "1px solid rgba(255,255,255,0.03)", textAlign: "right", fontWeight: 600 }}>{r.google}</div>
            <div style={{ color: TEXT, padding: "11px 0", borderBottom: "1px solid rgba(255,255,255,0.03)", textAlign: "right", fontWeight: 600 }}>{r.bing}</div>
          </div>
        ))}
      </div>
      {/* Share-of-total stacked bar */}
      <div style={{ marginTop: 18 }}>
        <div style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)", marginBottom: 7 }}>
          Share of total search clicks
        </div>
        <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", background: "rgba(255,255,255,0.04)" }}>
          <div style={{ width: "85%", background: GOOGLE }} />
          <div style={{ width: "15%", background: BING }} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 7, fontSize: 11, fontFamily: "var(--mono)", fontWeight: 600 }}>
          <span style={{ color: GOOGLE }}>Google 85%</span>
          <span style={{ color: BING }}>Bing 15%</span>
        </div>
      </div>
    </GlassCard>
  );
}

/* 10. MCP Server - ask your data inside your AI assistant */
function McpMockup() {
  return (
    <GlassCard style={{ padding: 0, overflow: "hidden" }}>
      {/* Connection header */}
      <div style={{
        padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", gap: 8,
      }}>
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#34d399", flexShrink: 0 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: TEXT, fontFamily: "var(--mono)" }}>ai-advisors</span>
        <span style={{ fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--mono)" }}>MCP connected</span>
      </div>
      <div style={{ padding: "16px 20px" }}>
        {/* User question */}
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
          <div style={{
            background: "rgba(255,255,255,0.05)", borderRadius: 10, padding: "8px 14px",
            maxWidth: "85%", fontSize: 13, color: TEXT, fontFamily: "var(--body)", lineHeight: 1.5,
          }}>
            What&#39;s my AI citation share this week?
          </div>
        </div>
        {/* Assistant reply, grounded in your data */}
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 6, flexShrink: 0,
            background: "rgba(79,205,196,0.15)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 14, fontWeight: 700, color: TEAL,
          }}>
            &#10022;
          </div>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
              {["Visibility", "Citations"].map((c) => (
                <span key={c} style={{
                  fontSize: 10, fontFamily: "var(--mono)", color: TEAL,
                  background: "rgba(79,205,196,0.12)", border: "1px solid rgba(79,205,196,0.25)",
                  borderRadius: 5, padding: "2px 7px",
                }}>{c}</span>
              ))}
            </div>
            <div style={{ fontSize: 13, color: TEXT_MUTED, fontFamily: "var(--body)", lineHeight: 1.6 }}>
              Your brand was cited in <span style={{ color: TEXT, fontWeight: 700 }}>34%</span> of tracked answers this week, <span style={{ color: "#34d399", fontWeight: 600 }}>up 6 points</span>. Perplexity and Claude drove the gain. ChatGPT still cites a competitor for <span style={{ color: TEXT }}>&ldquo;best CRM for startups.&rdquo;</span>
            </div>
          </div>
        </div>
      </div>
    </GlassCard>
  );
}


/* 11. HubSpot - Revenue from AI panel */
function HubSpotMockup() {
  // BY CAMPAIGN, because that is what the product renders. The section in the
  // app is literally headed "By campaign" and its rows are CampaignRow{name}.
  // This mockup previously showed revenue split by AI PLATFORM (ChatGPT Ads /
  // ChatGPT organic / Perplexity / Claude), a view that does not exist
  // anywhere: the only by-source data we hold is contact COUNTS in the daily
  // rollup, never revenue. It was promising a breakdown a customer would go
  // looking for and never find. Campaign names match the product screenshots
  // further down the page so the two tell one story.
  const rows = [
    { source: "FY26Q3 - United States - UMM-B2B", note: "closed won", amount: "$18.6K", deals: 3 },
    { source: "FY26Q3 - Canada - MM-B2B", note: "closed won", amount: "$14.2K", deals: 3 },
    { source: "FY26Q3 - United States - MM-B2B", note: "closed won", amount: "$9.6K", deals: 2 },
    { source: "Campaign unattributed", note: "no click id", amount: "$5.8K", deals: 1 },
  ];
  return (
    <GlassCard style={{ padding: 0, overflow: "hidden" }}>
      <div style={{
        padding: "14px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
      }}>
        <div style={{ fontFamily: "var(--display)", fontSize: 14, fontWeight: 600, color: TEXT }}>
          Revenue from AI
        </div>
        <div style={{
          padding: "6px 12px", borderRadius: 6,
          background: "rgba(79,205,196,0.12)", color: TEAL,
          fontSize: 11, fontWeight: 600, fontFamily: "var(--body)",
          display: "flex", alignItems: "center", gap: 6, flexShrink: 0,
        }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: TEAL }} />
          HubSpot connected
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        {[
          { label: "Closed-won from AI", value: "$48.2K" },
          { label: "Pipeline from AI", value: "$126K" },
          { label: "Customers from AI", value: "17" },
        ].map((kpi, i) => (
          <div key={kpi.label} style={{
            padding: "14px 16px",
            borderLeft: i > 0 ? "1px solid rgba(255,255,255,0.04)" : "none",
          }}>
            <div style={{ fontSize: 10, color: TEXT_MUTED, fontFamily: "var(--body)", marginBottom: 4 }}>{kpi.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: TEXT, fontFamily: "var(--display)" }}>{kpi.value}</div>
          </div>
        ))}
      </div>
      <div style={{ padding: "6px 20px 14px" }}>
        <div style={{
          display: "grid", gridTemplateColumns: "1fr 70px 50px",
          padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.06)",
          fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--mono)",
        }}>
          <div>Campaign</div>
          <div style={{ textAlign: "right" }}>Revenue</div>
          <div style={{ textAlign: "right" }}>Deals</div>
        </div>
        {rows.map((r, i) => (
          <div key={i} style={{
            display: "grid", gridTemplateColumns: "1fr 70px 50px",
            padding: "10px 0", borderBottom: i < rows.length - 1 ? "1px solid rgba(255,255,255,0.03)" : "none",
            alignItems: "baseline",
          }}>
            <div style={{ minWidth: 0 }}>
              <span style={{ fontSize: 12, color: TEXT, fontFamily: "var(--body)", fontWeight: 500 }}>{r.source}</span>
              <span style={{ fontSize: 10, color: TEXT_MUTED, fontFamily: "var(--body)", marginLeft: 6 }}>{r.note}</span>
            </div>
            <div style={{ fontSize: 12, color: TEXT, fontFamily: "var(--mono)", fontWeight: 600, textAlign: "right" }}>{r.amount}</div>
            <div style={{ fontSize: 12, color: TEXT_MUTED, fontFamily: "var(--mono)", textAlign: "right" }}>{r.deals}</div>
          </div>
        ))}
        <div style={{ marginTop: 10, fontSize: 11, color: TEXT_MUTED, fontFamily: "var(--body)" }}>
          Deal amounts from your HubSpot pipeline, matched to campaigns by click id
        </div>
      </div>
    </GlassCard>
  );
}

/* Mockup map - keyed by integration slug so detail pages and the hub
   resolve the same visual from data.js entries. */
const MOCKUPS = {
  "google-search-console": GSCMockup,
  "bing-webmaster-tools": BingMockup,
  "google-analytics-4": GA4Mockup,
  "google-ads": GoogleAdsMockup,
  "chatgpt-ads": ChatGPTAdsMockup,
  "google-tag-manager": GTMMockup,
  "slack": SlackMockup,
  "google-sheets": GoogleSheetsMockup,
  "wordpress": WordPressMockup,
  "mcp-server": McpMockup,
  "hubspot": HubSpotMockup,
};

export { GlassCard, MOCKUPS };
